import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import useEncryption from '../../hooks/cryptography/encryptText';
import StandardTextEditor from '../common/editor/standardTextEditor';
import ExpandButton from '../reportSubmission/fullscreenDescription/buttons/expandButton';
import DescriptionDialog from '../reportSubmission/fullscreenDescription/dialog/descriptionDialog';
import { Field } from '../ui/form';

import '../../assets/styles/formattedTextViewer.css';

import { AttachFileButton } from '~/pages/customer/reports/report-details/process-management/TaskDialog/ReportAttachment';
import { useForm } from '~/utils/forms';

interface WSSendMessage {
  message: string;
  to_user?: number;
  action: string;
  file_ids: number[];
}

interface ChatboxProps {
  disabled: boolean;
  onSendMessage: (chat: WSSendMessage) => void;
  conversationId: string;
}

// added schema to be able to use form without breaking old code
const composeNoteAdditionalSchema = z.object({
  files: z
    .array(
      z.object({
        file_id: z.number(),
        file_name: z.string(),
        is_uploaded: z.boolean(),
      }),
    )
    .optional(),
});

const Chatbox = ({ disabled, onSendMessage, conversationId }: ChatboxProps) => {
  const [text, setText] = useState('');

  const form = useForm({
    schema: composeNoteAdditionalSchema,
    defaultValues: {
      files: [],
    },
  });

  const [openDialog, setOpenDialog] = useState(false);
  const intl = useIntl();
  const { encryptText } = useEncryption();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const isTextEmpty = () => {
    return text.replace(/<[^>]*>?/gm, '').trim().length === 0;
  };

  const textChangeHandler = (content: string) => {
    setText(content);
  };

  const sendMessageHandler = () => {
    // Description in-transit encryption preparation
    const files = form.getValues('files');
    encryptText(text).then((encryptedText) => {
      const chat: WSSendMessage = {
        message: encryptedText,
        action: 'create',
        file_ids: files?.map((file) => file.file_id) ?? [],
      };
      onSendMessage(chat);
    });
    handleCloseDialog();
    form.reset();
    setText('');
  };

  const SendButton = () => (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'auto',
        gap: '10px',
      }}
    >
      <Field control={form.control} field='files'>
        <AttachFileButton
          type='message'
          conversationId={conversationId ?? ''}
        />
      </Field>
      <Button
        onClick={sendMessageHandler}
        sx={{
          width: '6.25rem',
          backgroundColor: '#2449FC',
          color: 'white',
          borderRadius: '33px',
          marginLeft: 'auto',
          height: '34px',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#4766fc',
          },
          '&:disabled': {
            color: 'white',
          },
          opacity: () => (isTextEmpty() ? 0.5 : 1),
        }}
        disabled={disabled || isTextEmpty()}
      >
        <FormattedMessage defaultMessage='Send' />
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '6px',
        border: '1px solid #DADADA',
        '&:focus-within': {
          border: '1px solid #181818',
        },
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          padding: '4px 12px',
          backgroundColor: '#F6F6F6',
          borderRadius: '6px 6px 0px 0px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Schibsted Grotesk',
            color: '#878787',
          }}
        ></Typography>
        <ExpandButton handleButtonClicked={handleOpenDialog} variant='dark' />
      </Stack>
      <DescriptionDialog
        text={text}
        onChangeText={textChangeHandler}
        open={openDialog}
        setClose={handleCloseDialog}
        variant='message'
        messageButton={<SendButton />}
        hideTitle
      />
      <StandardTextEditor
        text={text}
        onChangeText={textChangeHandler}
        customPlaceholder={intl.formatMessage({
          defaultMessage: 'Type here...',
        })}
        variant='message'
        messageButton={<SendButton />}
      />
    </Box>
  );
};

export default Chatbox;
